// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setRememberMeToken } from 'utils/local-storage';
import { toggleLanguage } from 'utils/i18n';
import { PRIVATE, ADMIN } from 'router/routes';
import { useSetToken, useUser } from 'utils/contexts/app-context';
import { canManageAccountSettings } from 'utils/accountSettingsPermissions';
import { clearAll } from 'state/ducks/global';
import ImagePlaceHolder from 'components/image-placeholder';
import { Flags } from 'providers/FlagsProvider';
import FeatureFlags from 'utils/feature-flags';
import styles from './styles.module.scss';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TuneIcon from '@mui/icons-material/Tune';
import SyncIcon from '@mui/icons-material/Sync';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useDarkMode } from 'providers/DarkModeProvider';
import { Auth } from 'aws-amplify';

type Props = {
  picture: ?string,
  clearState: () => void,
};

const ProfilePictureWithDropdown = ({
  picture,
  clearState,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const setToken = useSetToken();
  const user = useUser();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [settingsExpanded, setSettingsExpanded] = React.useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const { toggleDarkMode } = useDarkMode();
  const canManageSettings = canManageAccountSettings(user);

  const isOpen = Boolean(anchorEl);
  const displayName = user?.firstName?.trim() || '';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  const settingsNavItems = React.useMemo(() => {
    const items = [
      { label: t('nouns.profile'), path: PRIVATE.profile, icon: PersonOutlineIcon },
    ];
    if (canManageSettings) {
      items.push(
        { label: t('nouns.validation_plural'), path: PRIVATE.profileValidations, icon: TuneIcon },
        { label: t('nouns.live_validation'), path: PRIVATE.profileLiveUpdates, icon: SyncIcon },
      );
    }
    items.push({
      label: t('nouns.security_settings'),
      path: PRIVATE.profileSecurity,
      icon: LockOutlinedIcon,
    });
    return items;
  }, [canManageSettings, t]);

  const isSettingsNavActive = (path) => {
    if (path === PRIVATE.profile) {
      return location.pathname === PRIVATE.profile;
    }
    return location.pathname === path;
  };

  const activeSettingsItem = settingsNavItems.find((item) => isSettingsNavActive(item.path));

  React.useEffect(() => {
    if (!isOpen) {
      setSettingsExpanded(false);
    }
  }, [isOpen]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goTo = (path) => {
    navigate(path);
    handleClose();
  };

  const onUsersClick = () => {
    if (user.accessLevel === 'LEAD') {
      goTo(`/admin/team/${user.team_id}`);
    } else {
      goTo(ADMIN.delegates);
    }
  };

  const onLogout = async () => {
    try {
      await Auth.signOut({ global: true });
    } catch (err) {
      console.log('Cognito signOut not needed or failed:', err);
    }

    setRememberMeToken(null);
    setToken(null);
    clearState();
    localStorage.removeItem('filtering');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    sessionStorage.setItem('justLoggedOut', 'true');

    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    handleClose();
    window.location.href = '/login';
  };

  const onToggleLanguage = () => {
    handleClose();
    toggleLanguage();
  };

  const menuPaperSx = {
    width: 280,
    maxWidth: 'calc(100vw - 24px)',
    mt: 1,
    borderRadius: 2,
    overflow: 'hidden',
  };

  const settingsLabelSx = {
    lineHeight: 1.3,
    fontSize: 14,
    fontWeight: 600,
    color: 'text.primary',
    textTransform: 'none',
  };

  const menuItemSx = {
    py: 1,
    px: 2,
    gap: 1.5,
    fontSize: 14,
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: 'text.secondary',
    },
    '&.Mui-selected': {
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.12)' : 'rgba(25, 118, 210, 0.08)',
      fontWeight: 600,
      '& .MuiSvgIcon-root': {
        color: 'primary.main',
      },
    },
    '&.Mui-selected:hover': {
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.18)' : 'rgba(25, 118, 210, 0.12)',
    },
  };

  const settingsToggleSx = {
    ...menuItemSx,
    alignItems: 'center',
    justifyContent: 'flex-start',
  };

  return (
    <>
      <div className={styles.profile_picture_holder}>
        <div
          className={styles.profile_trigger}
          onClick={handleOpen}
          id="account_settings_circle"
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpen(event);
            }
          }}
        >
          <div className={styles.image_wrapper}>
            <ImagePlaceHolder src={picture} />
          </div>
          {displayName && (
            <span className={styles.profile_name}>{displayName}</span>
          )}
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: menuPaperSx, elevation: 8 } }}
      >
        <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={picture || undefined}
            alt=""
            sx={{ width: 44, height: 44, bgcolor: 'primary.light' }}
          >
            {(user?.firstName?.[0] || user?.email?.[0] || '?').toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {fullName && (
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {fullName}
              </Typography>
            )}
            {user?.email && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user.email}
              </Typography>
            )}
            {user?.company?.name && (
              <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
                {user.company.name}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => setSettingsExpanded((expanded) => !expanded)}
            sx={settingsToggleSx}
            aria-expanded={settingsExpanded}
          >
            <SettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography component="span" sx={settingsLabelSx} display="block">
                {t('nouns.settings')}
              </Typography>
              {!settingsExpanded && activeSettingsItem && (
                <Typography
                  variant="body2"
                  color="primary"
                  fontWeight={600}
                  noWrap
                  sx={{ mt: 0.25, textTransform: 'none' }}
                >
                  {activeSettingsItem.label}
                </Typography>
              )}
            </Box>
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: 'text.secondary',
                transform: settingsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </MenuItem>

          <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ pb: 0.5 }}>
              {settingsNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <MenuItem
                    key={item.path}
                    selected={isSettingsNavActive(item.path)}
                    onClick={() => goTo(item.path)}
                    sx={menuItemSx}
                  >
                    <Icon fontSize="small" />
                    {item.label}
                  </MenuItem>
                );
              })}
            </Box>
          </Collapse>
        </MenuList>

        {(user?.isAdmin || user?.accessLevel === 'LEAD' || canManageSettings) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuList dense disablePadding sx={{ py: 0.5 }}>
              {user && (user.isAdmin || user.accessLevel === 'LEAD') && (
                <MenuItem onClick={onUsersClick} sx={menuItemSx}>
                  <GroupOutlinedIcon fontSize="small" />
                  {t('nouns.user_management')}
                </MenuItem>
              )}

              <Flags authorizedFlags={[FeatureFlags.AllowBilling]}>
                {user && user.isAdmin && (
                  <MenuItem onClick={() => goTo(ADMIN.plans)} sx={menuItemSx}>
                    <PaymentOutlinedIcon fontSize="small" />
                    {t('nouns.plans_and_payments')}
                  </MenuItem>
                )}
              </Flags>

              <Flags authorizedFlags={[FeatureFlags.AllowIntegrations]}>
                {user && user.isAdmin && (
                  <MenuItem onClick={() => goTo(ADMIN.integrations)} sx={menuItemSx}>
                    <ExtensionOutlinedIcon fontSize="small" />
                    {t('nouns.integrations')}
                  </MenuItem>
                )}
              </Flags>

              {canManageSettings && (
                <MenuItem onClick={() => goTo(PRIVATE.usage)} sx={menuItemSx}>
                  <BarChartOutlinedIcon fontSize="small" />
                  {t('nouns.usage_and_credits')}
                </MenuItem>
              )}
            </MenuList>
          </>
        )}

        <Divider sx={{ my: 0.5 }} />

        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          <MenuItem onClick={onToggleLanguage} sx={menuItemSx}>
            <LanguageOutlinedIcon fontSize="small" />
            {t('nouns.switch_language')}
          </MenuItem>
          <MenuItem
            onClick={toggleDarkMode}
            sx={menuItemSx}
          >
            {theme.palette.mode === 'dark' ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
            {theme.palette.mode === 'dark'
              ? t('actions.switch_to_light_mode')
              : t('actions.switch_to_dark_mode')}
          </MenuItem>
        </MenuList>

        <Divider />

        <MenuList dense disablePadding>
          <MenuItem
            onClick={onLogout}
            sx={{
              ...menuItemSx,
              color: 'error.main',
              '& .MuiSvgIcon-root': { color: 'error.main' },
            }}
          >
            <LogoutOutlinedIcon fontSize="small" />
            {t('verbs.logout')}
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  clearState() {
    dispatch(clearAll());
  },
});

const ConnectedComponent = connect(
  null,
  mapDispatchToProps
)(ProfilePictureWithDropdown);

export default ConnectedComponent;
